import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import {
    IntegrationModulesService,
    IntegrationModuleTokenResponse,
} from '../../services/integration-modules.service';
import { ModuleService } from '../../services/module.service';

/**
 * Cuánto antes de `expiresAt` se dispara la renovación proactiva
 * (spec.md Escenario 9 — recuperación de sesión expirada sin que el
 * usuario pierda el contexto). Aclarado por el cliente (documento de
 * especificación de Tarifas, confirmado 2026-10-05): el token de emisión
 * real es de un solo uso y vive ~120s por defecto (ver `SESSION_DURATION_MS`
 * en el backend) — no 5 minutos como asumía el MOCK anterior. 30s de
 * margen sigue siendo razonable sobre ese número (75% del ciclo), pero la
 * duración real de la SESIÓN post-canje (la que de verdad importa acá) el
 * documento no la especifica — recalibrar si Tarifas la confirma más
 * adelante.
 */
const RENEW_MARGIN_MS = 30 * 1000;
const MIN_RENEW_DELAY_MS = 1000;

/**
 * TODO(OPB-263, 2026-10-15): renovación automática DESACTIVADA temporalmente.
 * Hoy "renovar" re-emite un pase nuevo y recarga el iframe, pero Tarifas ya
 * tiene sesiones reales de 1h + `POST /api/integration/embed-session/refresh
 * {sid}` (correo 2026-10-14). Rehacer la renovación sobre eso (en backend,
 * sin recargar el iframe, con la `session_api_key` del grupo) y volver a
 * poner esto en `true`.
 */
const AUTO_RENEW_ENABLED = false;

/**
 * Componente genérico para CUALQUIER módulo embebido cuyo grupo de menú
 * tenga `integration = true` (D14) — design.md D6/D17. Desde D17
 * (2026-10-08) resuelve `moduleCode` desde el PARÁMETRO de ruta
 * (`/integraciones/:moduleCode`, una sola entrada genérica en
 * `app.routes.ts`), no desde `route.data` — un módulo nuevo no necesita
 * tocar `app.routes.ts` en absoluto, solo crearse desde `/modules` con el
 * `menu_group` correcto. El título se busca por `code` en `ModuleService`
 * (el mismo listado que arma el sidebar) en vez de venir hardcodeado por
 * ruta.
 *
 * El `url` que arma el backend se usa tal cual — este componente no
 * concatena ningún parámetro (aclarado por el cliente, documento de
 * especificación de Tarifas confirmado 2026-10-05: la emisión real ya
 * devuelve la URL completa).
 *
 * Sobre "renovar": según ese mismo documento, no existe un mecanismo de
 * "extensión de sesión" separado — pedir un token nuevo (lo que hace
 * `renewToken()` de abajo) es la única forma de renovar, así que recargar
 * el iframe con la nueva URL en cada ciclo no es un defecto de este
 * componente, es el único camino que el proveedor ofrece hoy.
 */
@Component({
    selector: 'app-embedded-module',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './embedded-module.component.html',
    styleUrl: './embedded-module.component.scss',
})
export class EmbeddedModuleComponent implements OnInit, OnDestroy {
    moduleCode = '';
    moduleTitle = '';

    externalUrl = '';
    safeUrl: SafeResourceUrl | null = null;

    loading = true;
    /** Error al emitir el token inicial — no hay nada que mostrar todavía. */
    loadError: string | null = null;
    /**
     * Error al renovar — a diferencia de `loadError`, el iframe existente
     * NO se oculta (spec.md Escenario 9: sin perder contexto).
     */
    renewalError: string | null = null;

    private renewTimer: ReturnType<typeof setTimeout> | null = null;
    private routeDataSubscription?: Subscription;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly sanitizer: DomSanitizer,
        private readonly integrationModulesService: IntegrationModulesService,
        private readonly moduleService: ModuleService
    ) {}

    ngOnInit(): void {
        // paramMap (no snapshot): navegar de /integraciones/a a
        // /integraciones/b reusa esta misma instancia de componente (mismo
        // patrón de ruta) — hay que reaccionar al cambio, no solo leerlo una vez.
        this.routeDataSubscription = this.route.paramMap.subscribe((params) => {
            const moduleCode = params.get('moduleCode') ?? '';
            if (moduleCode && moduleCode !== this.moduleCode) {
                this.moduleCode = moduleCode;
                this.moduleTitle = moduleCode;
                this.loadModuleTitle(moduleCode);
                this.clearRenewTimer();
                this.loadToken();
            }
        });
    }

    /** Título real desde el mismo listado que arma el sidebar — si falla, se queda con el código (ya seteado en ngOnInit). */
    private loadModuleTitle(moduleCode: string): void {
        this.moduleService.getModules().subscribe({
            next: (res) => {
                const match = res.data?.find((m) => m.code === moduleCode);
                if (match && this.moduleCode === moduleCode) {
                    this.moduleTitle = match.name;
                }
            },
            error: () => undefined,
        });
    }

    ngOnDestroy(): void {
        this.clearRenewTimer();
        this.routeDataSubscription?.unsubscribe();
        // Best-effort: si no había sesión, el backend responde revoked:false
        // sin error (D8 en design.md) — nunca bloquea la navegación.
        if (this.moduleCode) {
            this.integrationModulesService
                .revokeToken(this.moduleCode)
                .subscribe({ error: () => undefined });
        }
    }

    retryLoad(): void {
        this.loadError = null;
        this.loadToken();
    }

    retryRenewal(): void {
        this.renewalError = null;
        this.renewToken();
    }

    private loadToken(): void {
        this.loading = true;
        this.loadError = null;
        this.integrationModulesService.getToken(this.moduleCode).subscribe({
            next: (res) => this.applyToken(res),
            error: (err) => {
                this.loading = false;
                this.loadError = this.describeError(err);
            },
        });
    }

    private renewToken(): void {
        this.integrationModulesService.renewToken(this.moduleCode).subscribe({
            next: (res) => {
                this.renewalError = null;
                this.applyToken(res);
            },
            error: (err) => {
                // spec.md Escenario 9: mensaje accionable, sin ocultar el
                // iframe existente — el usuario no pierde el contexto.
                this.renewalError = this.describeError(err);
            },
        });
    }

    private applyToken(res: IntegrationModuleTokenResponse): void {
        this.loading = false;
        this.externalUrl = res.url;
        this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.externalUrl);
        this.scheduleRenewal(res.expiresAt);
    }

    private scheduleRenewal(expiresAtIso: string): void {
        this.clearRenewTimer();
        if (!AUTO_RENEW_ENABLED) {
            return;
        }
        const expiresAt = new Date(expiresAtIso).getTime();
        const delay = Math.max(
            expiresAt - Date.now() - RENEW_MARGIN_MS,
            MIN_RENEW_DELAY_MS
        );
        this.renewTimer = setTimeout(() => this.renewToken(), delay);
    }

    private clearRenewTimer(): void {
        if (this.renewTimer) {
            clearTimeout(this.renewTimer);
            this.renewTimer = null;
        }
    }

    private describeError(err: {
        status?: number;
        error?: { message?: string };
    }): string {
        const message = err?.error?.message;
        if (message) {
            return message;
        }
        if (err?.status === 403) {
            return 'No tiene autorización para acceder a este módulo.';
        }
        if (err?.status === 404) {
            return 'El módulo no está disponible en este momento.';
        }
        return 'No se pudo conectar con el módulo. Puede reintentar o abrirlo en una nueva pestaña.';
    }
}
